import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import apiClient from '../utils/api';
import { fetchConfig } from '../config';

// Global state for token refresh
let isRefreshing = false;
let failedQueue = [];
let authContextRef = null;
let configCache = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const clearAuthData = async () => {
  const cfg = configCache || await fetchConfig();
  const keyPrefix = `CognitoIdentityServiceProvider.${cfg.cognito.clientId}`;
  const lastAuthUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
  
  if (lastAuthUser) {
    localStorage.removeItem(`${keyPrefix}.${lastAuthUser}.idToken`);
    localStorage.removeItem(`${keyPrefix}.${lastAuthUser}.accessToken`);
    localStorage.removeItem(`${keyPrefix}.${lastAuthUser}.refreshToken`);
    localStorage.removeItem(`${keyPrefix}.LastAuthUser`);
  }
  
  delete apiClient.defaults.headers.common['Authorization'];
};

const redirectToLogin = () => {
  clearAuthData();
  if (authContextRef?.logout) {
    authContextRef.logout();
  }
  window.location.href = '/login';
};

// Configure interceptors for our API client
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      console.log(`🔄 ${error.response?.status} error detected, attempting token refresh...`);
      
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      return fetchConfig().then(cfg => {
        const keyPrefix = `CognitoIdentityServiceProvider.${cfg.cognito.clientId}`;
        const lastAuthUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
        const refreshToken = lastAuthUser ? localStorage.getItem(`${keyPrefix}.${lastAuthUser}.refreshToken`) : null;
        
        if (refreshToken) {
          const refreshClient = axios.create({
            baseURL: apiClient.defaults.baseURL
          });
          
          return refreshClient.post('/auth/refresh', {
            refresh_token: refreshToken
          }).then(response => {
            const { access_token, id_token, refresh_token: newRefreshToken, user } = response.data;
            
            localStorage.setItem(`${keyPrefix}.${lastAuthUser}.idToken`, id_token);
            if (access_token) localStorage.setItem(`${keyPrefix}.${lastAuthUser}.accessToken`, access_token);
            if (newRefreshToken) localStorage.setItem(`${keyPrefix}.${lastAuthUser}.refreshToken`, newRefreshToken);
            
            if (authContextRef?.setToken) {
              authContextRef.setToken(id_token);
            }
            
            if (authContextRef?.setUser && user) {
              authContextRef.setUser(user);
            }
            
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${id_token}`;
            processQueue(null, id_token);
            
            originalRequest.headers['Authorization'] = `Bearer ${id_token}`;
            return apiClient(originalRequest);
          }).catch(refreshError => {
            console.error('🔄 Token refresh failed:', refreshError);
            processQueue(refreshError, null);
            clearAuthData().then(() => {
              if (authContextRef?.logout) {
                authContextRef.logout();
              }
              window.location.href = '/login';
            });
            return Promise.reject(refreshError);
          }).finally(() => {
            isRefreshing = false;
          });
        } else {
          console.log('🔄 No refresh token found, redirecting to login without clearing tokens');
          isRefreshing = false;
          if (authContextRef?.logout) {
            authContextRef.logout();
          }
          window.location.href = '/login';
          return Promise.reject(error);
        }
      });
    }
    
    return Promise.reject(error);
  }
);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('AuthProvider: Initializing...');
    
    // Load config and check for tokens
    fetchConfig().then(cfg => {
      configCache = cfg;
      
      const keyPrefix = `CognitoIdentityServiceProvider.${cfg.cognito.clientId}`;
      const lastAuthUser = localStorage.getItem(`${keyPrefix}.LastAuthUser`);
      console.log('AuthProvider: keyPrefix:', keyPrefix);
      console.log('AuthProvider: lastAuthUser:', lastAuthUser);
      
      if (lastAuthUser) {
        const storedIdToken = localStorage.getItem(`${keyPrefix}.${lastAuthUser}.idToken`);
        const storedRefreshToken = localStorage.getItem(`${keyPrefix}.${lastAuthUser}.refreshToken`);
        console.log('AuthProvider: Found Cognito SDK token for user:', lastAuthUser);
        console.log('AuthProvider: Has idToken:', !!storedIdToken);
        console.log('AuthProvider: Has refreshToken:', !!storedRefreshToken);
        
        if (storedIdToken) {
          console.log('AuthProvider: Setting token from localStorage');
          setToken(storedIdToken);
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${storedIdToken}`;
        } else if (!storedIdToken && !storedRefreshToken) {
          console.log('AuthProvider: No tokens found, user needs to login');
        }
      } else {
        console.log('AuthProvider: No lastAuthUser found');
      }
      
      const urlParams = new URLSearchParams(window.location.search);
      const loginToken = urlParams.get('token');
      if (loginToken && !lastAuthUser) {
        login(loginToken);
      } else {
        console.log('AuthProvider: Setting loading to false');
        setLoading(false);
      }
    });
  }, []);

  useEffect(() => {
    console.log('AuthProvider: Token changed:', token ? 'exists' : 'null');
    if (token) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  const login = async (loginToken) => {
    try {
      const response = await apiClient.post('/auth/login', { token: loginToken });
      const { access_token, user_id } = response.data;
      
      setToken(access_token);
      setUser({ id: user_id });
      
      // Store in Cognito SDK format
      const config = await fetchConfig();
      const keyPrefix = `CognitoIdentityServiceProvider.${config.cognito.clientId}`;
      const username = user_id;
      
      localStorage.setItem(`${keyPrefix}.LastAuthUser`, username);
      localStorage.setItem(`${keyPrefix}.${username}.idToken`, access_token);
      
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      window.history.replaceState({}, document.title, window.location.pathname);
      
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      setLoading(false);
      return false;
    }
  };

  const handleCallback = async (code) => {
    try {
      const response = await apiClient.post('/auth/cognito-callback', { 
        code,
        redirect_uri: window.location.origin + '/auth/callback.html'
      });
      const { access_token, id_token, refresh_token, user } = response.data;
      
      setToken(id_token);
      setUser(user);
      
      // Store tokens in Cognito SDK format
      const config = await fetchConfig();
      const keyPrefix = `CognitoIdentityServiceProvider.${config.cognito.clientId}`;
      const username = user.email || user.sub;
      
      localStorage.setItem(`${keyPrefix}.LastAuthUser`, username);
      localStorage.setItem(`${keyPrefix}.${username}.idToken`, id_token);
      if (access_token) localStorage.setItem(`${keyPrefix}.${username}.accessToken`, access_token);
      if (refresh_token) localStorage.setItem(`${keyPrefix}.${username}.refreshToken`, refresh_token);
      
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${id_token}`;
      
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Callback processing failed:', error);
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    setToken(null);
    setUser(null);
    await clearAuthData();
    localStorage.setItem('justLoggedOut', 'true');
    
    // Simply redirect to login page without Cognito logout
    // This clears the session locally
    window.location.href = '/auth';
  };

  const value = {
    token,
    user,
    login,
    logout,
    loading,
    setToken,
    setUser,
    handleCallback
  };

  authContextRef = value;

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};